/**
 * Pieces of the public Studio demo (?demo=1): the source banner, the sign-up card that stands in
 * for features needing an account, and the export prompt. See ../demo.ts.
 */
import { ExternalLink, X } from 'lucide-react'
import type { VideoOriginal } from '../types'
import { DEMO_PRO_URL, DEMO_SIGNUP_URL } from '../demo'

/** Credit + the way back to the whole talk. Every demo links its source (operator rule). */
export function DemoBanner({ original }: { original?: VideoOriginal }) {
  return (
    <div className="shrink-0 flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 md:px-4 py-2 text-xs border-b border-indigo-500/20 bg-gradient-to-r from-indigo-950/80 via-[#11131c] to-fuchsia-950/60">
      <span className="px-1.5 py-0.5 rounded bg-indigo-500/25 text-indigo-200 font-semibold uppercase tracking-wide text-[10px]">Live demo</span>
      {original ? (
        <span className="text-gray-300">
          “{original.title}” by {original.speaker}{original.event ? ` at ${original.event}` : ''}, analyzed and clipped by legit2clip.{' '}
          <a href={original.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-indigo-300 hover:text-white font-medium underline underline-offset-2">
            {original.cta || 'Watch the whole video here'} <ExternalLink size={11} />
          </a>
        </span>
      ) : (
        <span className="text-gray-300">A real video, analyzed and clipped by legit2clip.</span>
      )}
      <span className="ml-auto flex items-center gap-2">
        <a href={DEMO_PRO_URL} className="px-2.5 py-1 rounded-lg bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white font-medium hover:opacity-90 whitespace-nowrap">Use Studio on your video</a>
      </span>
    </div>
  )
}

/** Stands in for anything that needs an account: the assistant, generation, the library. */
export function DemoGate({ what }: { what: string }) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-xs text-center">
        <div className="mx-auto mb-3 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-lg">✦</div>
        <div className="text-sm font-semibold text-white">{what} works on your own videos</div>
        <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
          This demo is read-only. With Studio you can ask the assistant for edits, generate voiceovers, music and sound effects, and export finished shorts.
        </p>
        <a href={DEMO_PRO_URL} className="mt-4 inline-block w-full py-2 text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white font-medium hover:opacity-90">Get Studio with Pro</a>
        <a href={DEMO_SIGNUP_URL} className="mt-2 block text-xs text-gray-400 hover:text-white">or start free: automatic clips from a long video</a>
      </div>
    </div>
  )
}

/** What "Export" does in the demo: show what would be made, then the way to make it for real. */
export function DemoExportModal({ count, original, onClose }: { count: number; original?: VideoOriginal; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0e1017] p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-gray-500 hover:text-white" aria-label="Close"><X size={18} /></button>
        <div className="text-lg font-semibold text-white">Ready to render {count} short{count === 1 ? '' : 's'}</div>
        <p className="mt-2 text-sm text-gray-400 leading-relaxed">
          In Studio, Export renders each highlight you picked as a vertical clip with burned-in captions in your chosen style, on our GPU cloud, usually within a few minutes.
        </p>
        {original && (
          <p className="mt-3 text-xs text-gray-500">
            Clips from this demo come from “{original.title}” by {original.speaker}.{' '}
            <a href={original.url} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-white underline underline-offset-2">{original.cta || 'Watch the whole video here'}</a>
          </p>
        )}
        <a href={DEMO_PRO_URL} className="mt-5 block w-full py-2.5 text-center text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white font-semibold hover:opacity-90">Get Studio with Pro, $19/month</a>
        <a href={DEMO_SIGNUP_URL} className="mt-2.5 block text-center text-xs text-gray-400 hover:text-white">or start free: 5 hours of video a month, automatic clips</a>
      </div>
    </div>
  )
}
